import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight } from 'lucide-react';

const transitions = [
  { pain: 'Career Confusion', solution: 'Structured Career Alignment', desc: 'Clear guidance on streams, subjects, colleges, and career paths that align with your strengths and interests.' },
  { pain: 'Academic Pressure', solution: 'Personalized Learning Strategy', desc: 'Tailored study approaches that work for your learning style, not against it.' },
  { pain: 'Emotional Uncertainty', solution: 'Emotional Regulation Insights', desc: 'Understanding your emotional patterns to build resilience and confidence.' },
  { pain: 'Parent-Child Disconnect', solution: 'Shared Understanding', desc: 'Reports and counseling sessions designed to align parent and child perspectives.' },
  { pain: 'Decision Paralysis', solution: 'Decision Confidence', desc: 'Scientific clarity replaces guesswork with structured, supported career and education choices.' },
];

const ProblemSolution = () => {
  const [active, setActive] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) setVisible(true);
    }, { threshold: 0.1 });
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!autoPlay) return;
    const timer = setInterval(() => setActive(a => (a + 1) % transitions.length), 2800);
    return () => clearInterval(timer);
  }, [autoPlay]);

  return (
    <section
      ref={sectionRef}
      style={{ padding: '100px 24px', background: '#F9F9F7', position: 'relative', overflow: 'hidden' }}
      data-testid="problem-solution-section"
    >
      {/* Background circle */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        width: '700px', height: '700px',
        background: 'radial-gradient(ellipse, rgba(42,157,143,0.05) 0%, transparent 65%)',
        borderRadius: '50%', pointerEvents: 'none',
      }} />

      <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative' }}>
        <div className={`reveal ${visible ? 'visible' : ''}`} style={{ textAlign: 'center', marginBottom: '64px' }}>
          <div className="section-label">The Transformation</div>
          <div className="section-divider" />
          <h2 style={{
            fontFamily: 'Manrope', fontWeight: 700, fontSize: 'clamp(30px,3.8vw,46px)',
            color: '#1A202C', margin: '20px 0 0', letterSpacing: '-0.02em',
          }}>
            From Confusion to <span style={{ color: '#2A9D8F' }}>Clarity</span>
          </h2>
        </div>

        <div className="problem-solution-grid" style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '40px', alignItems: 'center' }}>

          {/* PAIN side */}
          <div className={`reveal ${visible ? 'visible' : ''}`} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ fontSize: '12px', fontFamily: 'Manrope', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#94A3B8', marginBottom: '4px' }}>
              The Challenge
            </div>
            {transitions.map((t, i) => (
              <button
                key={t.pain}
                onClick={() => { setActive(i); setAutoPlay(false); }}
                data-testid={`pain-item-${i}`}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '14px 18px', borderRadius: '14px', cursor: 'pointer',
                  border: active === i ? '1.5px solid rgba(244,162,97,0.4)' : '1.5px solid transparent',
                  background: active === i ? 'rgba(244,162,97,0.08)' : 'rgba(255,255,255,0.5)',
                  transition: 'all 0.25s ease',
                  fontFamily: 'Manrope', fontWeight: active === i ? 600 : 400,
                  fontSize: '15px', color: active === i ? '#F4A261' : '#94A3B8',
                  textDecoration: active !== i ? 'line-through' : 'none',
                }}
              >
                {t.pain}
              </button>
            ))}
          </div>

          {/* Center arrow */}
          <div className={`reveal reveal-delay-3 ${visible ? 'visible' : ''}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '52px', height: '52px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #2A9D8F, #8DA399)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(42,157,143,0.3)',
            }}>
              <ArrowRight size={22} color="white" />
            </div>
            <div style={{ fontSize: '10px', fontFamily: 'Manrope', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#2A9D8F', textAlign: 'center' }}>
              MindVrik
            </div>
          </div>

          {/* SOLUTION side */}
          <div className={`reveal reveal-delay-2 ${visible ? 'visible' : ''}`}>
            <div style={{ fontSize: '12px', fontFamily: 'Manrope', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#94A3B8', marginBottom: '16px' }}>
              The Transformation
            </div>
            <div
              key={active}
              style={{
                background: 'rgba(255,255,255,0.85)',
                backdropFilter: 'blur(16px)',
                border: '1.5px solid rgba(42,157,143,0.18)',
                borderRadius: '20px',
                padding: '32px',
                boxShadow: '0 8px 32px rgba(42,157,143,0.1)',
                animation: 'fade-in-up 0.4s ease forwards',
              }}
            >
              <div style={{ fontSize: '11px', fontFamily: 'Manrope', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#2A9D8F', marginBottom: '10px' }}>
                {String(active + 1).padStart(2, '0')} / {String(transitions.length).padStart(2, '0')}
              </div>
              <h3 style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: '22px', color: '#1A202C', margin: '0 0 12px' }}>
                {transitions[active].solution}
              </h3>
              <p style={{ fontFamily: 'Inter', fontSize: '15px', color: '#64748B', lineHeight: 1.7, margin: 0 }}>
                {transitions[active].desc}
              </p>

              {/* Progress indicator */}
              <div style={{ display: 'flex', gap: '6px', marginTop: '24px' }}>
                {transitions.map((_, j) => (
                  <button
                    key={j}
                    onClick={() => { setActive(j); setAutoPlay(false); }}
                    style={{
                      height: '3px',
                      flex: active === j ? 3 : 1,
                      background: active === j ? '#2A9D8F' : 'rgba(42,157,143,0.2)',
                      borderRadius: '99px', border: 'none', cursor: 'pointer',
                      transition: 'all 0.3s ease',
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSolution;
